import React, {FC, PropsWithChildren} from 'react';
import {Link} from 'react-router-dom';

import {EventStatus, GenericProps, MatchSimple, Sport, PeriodScore} from '../../../utils/types';

import './styles.scss';

// BIQ: split this file & documentation

export interface MatchListItemProps extends GenericProps {
  match: MatchSimple;
  hasDate?: boolean;
}

interface MatchListItemWrapperProps extends GenericProps {
  id: string;
  slug: string;
}

const MatchListItemWrapper: FC<PropsWithChildren<MatchListItemWrapperProps>> = ({
  id,
  slug,
  children,
  className,
}: PropsWithChildren<MatchListItemWrapperProps>) =>
  id ? (
    <Link to={`/${slug}/${id}/`} className={`MatchListItem__wrapper ${className ?? ''}`}>
      {children}
    </Link>
  ) : (
    <div className={`MatchListItem__wrapper ${className ?? ''}`}>{children}</div>
  );

const PeriodScores: FC<{
  periodScores: PeriodScore[];
  teamKey: keyof Pick<PeriodScore, 'homeScore' | 'awayScore'>;
  status: EventStatus;
}> = ({
  periodScores,
  teamKey,
  status,
}: {
  periodScores: PeriodScore[];
  teamKey: keyof Pick<PeriodScore, 'homeScore' | 'awayScore'>;
  status: EventStatus;
}) => (
  <>
    {periodScores.length
      ? periodScores.map(ps => {
          const opponentKey = teamKey === 'awayScore' ? 'homeScore' : 'awayScore';
          const teamScore = ps[teamKey];
          const oppScore = ps[opponentKey];

          const winning = teamScore && oppScore && teamScore > oppScore;

          return (
            <span
              className={`MatchListItem__periodScore ${
                winning ? 'MatchListItem__periodScore--winning' : ''
              }`}
              key={ps.periodNumber}
            >
              {teamKey in ps && typeof ps[teamKey] !== 'undefined' ? ps[teamKey] : '-'}
            </span>
          );
        })
      : new Array(5).map(([v, i]) => (
          <span className="MatchListItem__periodScore" key={`nullscore-${i}`}>
            -
          </span>
        ))}
  </>
);

const MatchListItem: FC<MatchListItemProps> = ({
  match: {
    _id,
    sport,
    sportEventSlug,
    date,
    teams,
    liveBroadcast,
    status,
    minute,
    matchStatus,
    stoppageTime,
    score,
  },
  hasDate,
  className,
}: MatchListItemProps) => (
  <article
    className={`MatchListItem MatchListItem--${sport} ${
      status === EventStatus.LIVE ? 'MatchListItem--live' : ''
    } ${className ?? ''}`}
  >
    <MatchListItemWrapper className="MatchListItem__wrapper" id={_id} slug={sportEventSlug}>
      <div className="MatchListItem__header">
        {hasDate || status === EventStatus.NOT_STARTED ? (
          <>
            <span className="MatchListItem__date">
              {date.getDate()} {date.toLocaleString('el-GR', {month: 'short'})}
            </span>
            {hasDate ? (
              <span className="MatchListItem__year">{date.getFullYear()}</span>
            ) : (
              <span className="MatchListItem__time">{`${date.getHours()}:${date
                .getMinutes()
                .toString()
                .padStart(2, '0')}`}</span>
            )}
          </>
        ) : (
          <>
            <span className="MatchListItem__status">{matchStatus === null ? 'ΑΝΑΒΟΛΗ' : matchStatus}</span>
            { 
              /* eslint-disable no-nested-ternary */
              status === EventStatus.LIVE &&
                minute !== null &&
                minute !== undefined &&
                minute !== 0 &&
                matchStatus !== 'ΗΜΙ' &&
                matchStatus !== 'BREAK' &&
                (
                  <span className="MatchListItem__minute">
                    {minute}
                    {stoppageTime ? ` + ${stoppageTime}` : ''}&apos;
                  </span>
                )
            }
          </>
        )}
      </div>
      <div className="MatchListItem__content">
        <div
          className={`MatchListItem__team MatchListItem__team--home ${
            score[0] > score[1] ? 'MatchListItem__team--winning' : ''
          }`}
        >
          <span className="MatchListItem__teamName">
            {hasDate ? teams[0].shortname : teams[0].fullname}
          </span>
          <span className="MatchListItem__teamScore">
            {status === EventStatus.NOT_STARTED ? '-' : score[0]}
          </span>
        </div>

        <div
          className={`MatchListItem__team MatchListItem__team--away ${
            score[0] < score[1] ? 'MatchListItem__team--winning' : ''
          }`}
        >
          <span className="MatchListItem__teamName">
            {hasDate ? teams[1].shortname : teams[1].fullname}
          </span>
          <span className="MatchListItem__teamScore">
            {status === EventStatus.NOT_STARTED ? '-' : score[1]}
          </span>
        </div>
        {liveBroadcast?.activity === true && <div className="MatchListItem__broadcast">
          <svg xmlns="http://www.w3.org/2000/svg" width="12.75" height="10" viewBox="0 0 12.75 10"><g data-name="Group 7949"><g data-name="Group 7939" fill="#ff0040"><path data-name="Path 2502" d="M6.25 9.998v-10l-6.25 5Z"/><path data-name="Rectangle 4810" d="M0 3.125h3.75v3.75H0z"/></g><g data-name="Group 7947" fill="none" stroke="#ff0040"><path data-name="Path 2506" d="M8.252 1.004a4 4 0 0 1 0 8"/><path data-name="Path 2507" d="M8.252 3.004a2 2 0 0 1 0 4"/></g></g></svg>
          <span>ΗΧΗΤΙΚΗ ΜΕΤΑΔΟΣΗ</span>
          </div>}
      </div>
    </MatchListItemWrapper>
  </article>
);

export default MatchListItem;
